/**
 * CrmLogInteractionModal — log an Interaction against a lead (or opportunity). Team Member defaults
 * to the current member. Used from the lead detail modal; reusable for opportunities (pass projectId).
 */
import { useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { ctx } from '../../outline/ctx.js'
import { alertDialog } from '../Dialog.jsx'
import {
  logInteraction, INTERACTION_TYPES, LEAD_ACTIVITY_TYPES, OPPORTUNITY_TYPES, PLANNED_NEXT_TYPES,
} from '../../services/interactions.js'

function todayISO() {
  // local date (YYYY-MM-DD) for the date input default
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

export default function CrmLogInteractionModal({ leadId, projectId, onClose, onLogged, prefill, title }) {
  const forOpportunity = !!projectId && !leadId
  // Neither link — a bare prospecting touch (new-business outreach not yet tied to a tracked Lead
  // or Opportunity). Neither the Lead nor Opportunity activity-type vocabulary fits, so that
  // dropdown is simply omitted rather than forced into the wrong shape.
  const bareProspecting = !leadId && !projectId
  const pf = prefill || {}
  const [type, setType] = useState(pf.type || 'Phone Call')
  const [activity, setActivity] = useState(pf.leadActivityType || pf.opportunityType || '')
  const [date, setDate] = useState(todayISO())
  const [notes, setNotes] = useState(pf.notes || '')
  const [plannedNext, setPlannedNext] = useState('')
  const [nextDate, setNextDate] = useState('')
  const [saving, setSaving] = useState(false)

  const activityOptions = forOpportunity ? OPPORTUNITY_TYPES : LEAD_ACTIVITY_TYPES
  const activityLabel = forOpportunity ? 'Opportunity Type' : 'Lead Activity Type'

  async function save() {
    if (saving) return
    setSaving(true)
    try {
      const res = await logInteraction({
        leadId, projectId,
        type,
        leadActivityType: (bareProspecting || forOpportunity) ? undefined : (activity || undefined),
        opportunityType: (!bareProspecting && forOpportunity) ? (activity || undefined) : undefined,
        notes: notes || undefined,
        date: date ? new Date(date + 'T12:00:00').toISOString() : undefined,
        plannedNext: plannedNext || undefined,
        nextDate: nextDate || undefined,
        teamMemberId: (ctx.currentMember && ctx.currentMember.id) || undefined,
      })
      if (onLogged) onLogged(res)
      onClose && onClose()
    } catch (e) {
      await alertDialog({ title: 'Log failed', message: (e && e.message) || 'Could not log the interaction.' })
    } finally {
      setSaving(false)
    }
  }

  const footer = (
    <>
      <button style={F.btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
      <button style={F.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Logging…' : 'Log interaction'}</button>
    </>
  )

  return (
    <CrmModal title={title || 'Log interaction'} onClose={onClose} footer={footer} width={520}>
      <div style={F.grid2}>
        <div style={F.row}>
          <label style={F.label}>Type</label>
          <select style={F.input} value={type} onChange={e => setType(e.target.value)}>
            {INTERACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={F.row}>
          <label style={F.label}>Date</label>
          <input style={F.input} type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
      </div>
      {!bareProspecting && (
        <div style={F.row}>
          <label style={F.label}>{activityLabel}</label>
          <select style={F.input} value={activity} onChange={e => setActivity(e.target.value)}>
            <option value="">—</option>
            {activityOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}
      <div style={F.row}>
        <label style={F.label}>Notes</label>
        <textarea style={F.textarea} value={notes} onChange={e => setNotes(e.target.value)} placeholder="What happened?" />
      </div>
      <div style={F.grid2}>
        <div style={F.row}>
          <label style={F.label}>Planned next</label>
          <select style={F.input} value={plannedNext} onChange={e => setPlannedNext(e.target.value)}>
            <option value="">—</option>
            {PLANNED_NEXT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={F.row}>
          <label style={F.label}>Next date</label>
          <input style={F.input} type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} />
        </div>
      </div>
    </CrmModal>
  )
}
